/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { PromiseDebouncer } from '../../libs/PromiseDebouncer';

/* eslint-disable import/prefer-default-export */

export class UserService {
    constructor(dto) {
        if (!dto) {
            throw new Error(
                `dto is required when instantiating a new UserService`
            );
        }

        [`userAdapter`].forEach((name) => {
            if (!dto[name]) {
                throw new Error(
                    `${name} property is required when instantiating a new UserService`
                );
            }
        });

        this.promiseDebouncer = new PromiseDebouncer();
        this.userAdapter = dto.userAdapter;
    }

    /**
     * Get user.
     * @param {string} userId The user id.
     *
     * @returns {Promise<object>} A promised user.
     */
    get(userId) {
        return this.promiseDebouncer.debounce(
            {
                method: `get`,
                userId,
            },
            () => this.userAdapter.get(userId)
        );
    }

    /**
     * Update an user with new values.
     *
     * @param {string} documentId The user id.
     * @param {object} user The user data to replace.
     *
     * @returns {Promise<object>} The result of the update.
     */
    setAll(user, documentId) {
        return this.promiseDebouncer.debounce(
            {
                method: `get`,
                documentId,
            },
            () => this.userAdapter.setAll(user, documentId)
        );
    }

    /**
     * Method will toggle given key in array.
     *
     * @param {string} notificationKey Notification key we need to toggle.
     * @param {object} user User object.
     *
     * @return {Promise<object>} The result of the update.
     */
    updateNotifications(notificationKey, user) {
        return this.promiseDebouncer.debounce(
            {
                method: `get`,
                notificationKey,
            },
            () => this.userAdapter.updateNotifications(notificationKey, user)
        );
    }

    /**
     * Method will toggle push notifications on/off.
     *
     * @param {bool} toggle Flag determining if notifications should be on or off.
     * @param {string} documentId For multiple promises only.
     *
     * @return {Promise<object>} The result of the update.
     */
    toggleNotifications(toggle, documentId) {
        return this.promiseDebouncer.debounce(
            {
                method: `get`,
                documentId,
            },
            () => this.userAdapter.toggleNotifications(toggle)
        );
    }
}
